import { type ColumnType, type Selectable } from 'kysely'
import { z } from 'zod'

import {
  forecastModelTypeId,
  type ForecastModelTypeId,
} from './ForecastModelType.js'
import {
  labelSortType,
  type default as LabelSortType,
} from './LabelSortType.js'
import { legacySiteId, type LegacySiteId } from './LegacySite.js'
import {
  lengthUnitType,
  type default as LengthUnitType,
} from './LengthUnitType.js'
import { organizationId, type OrganizationId } from './Organization.js'
import { projectId, type ProjectId } from './Project.js'
import { siteLayoutId, type SiteLayoutId } from './SiteLayout.js'
import { sitePageTypeId, type SitePageTypeId } from './SitePageType.js'
import {
  siteStatusType,
  type default as SiteStatusType,
} from './SiteStatusType.js'
import { tempUnitType, type default as TempUnitType } from './TempUnitType.js'
import { userId, type UserId } from './User.js'

/** Represents the view public.site_with_history_view_ */
export default interface SiteWithHistoryViewTable {
  /** Database type: pg_catalog.int4 */
  id: ColumnType<LegacySiteId, never, never>

  /** Database type: pg_catalog.text */
  siteName: ColumnType<string, never, never>

  /** Database type: pg_catalog.bool */
  visible: ColumnType<boolean | null, never, never>

  /** Database type: public.site_status_type_ */
  status: ColumnType<SiteStatusType | null, never, never>

  /** Database type: pg_catalog.int4 */
  sortOrder: ColumnType<number | null, never, never>

  /** Database type: pg_catalog.bool */
  includesArchivedData: ColumnType<boolean | null, never, never>

  /** Database type: pg_catalog.text */
  internalNotes: ColumnType<string | null, never, never>

  /** Database type: pg_catalog.text */
  customerNotes: ColumnType<string | null, never, never>

  /** Database type: public.length_unit_type_ */
  lengthUnits: ColumnType<LengthUnitType, never, never>

  /** Database type: public.temp_unit_type_ */
  tempUnits: ColumnType<TempUnitType, never, never>

  /** Database type: pg_catalog.numeric */
  geoLat: ColumnType<number | null, never, never>

  /** Database type: pg_catalog.numeric */
  geoLon: ColumnType<number | null, never, never>

  /** Database type: pg_catalog.numeric */
  geoElev: ColumnType<number | null, never, never>

  /** Database type: pg_catalog.bool */
  plotSnow: ColumnType<boolean | null, never, never>

  /** Database type: pg_catalog.numeric */
  sonicHeight: ColumnType<number | null, never, never>

  /** Database type: pg_catalog.bool */
  labelSettingShowCableSerial: ColumnType<boolean | null, never, never>

  /** Database type: public.label_sort_type_ */
  labelSettingSortBy: ColumnType<LabelSortType | null, never, never>

  /** Database type: pg_catalog.int4 */
  readingsVersion: ColumnType<number | null, never, never>

  /** Database type: pg_catalog.bool */
  useRecordDate: ColumnType<boolean | null, never, never>

  /** Database type: pg_catalog.bool */
  showSdmLines: ColumnType<boolean | null, never, never>

  /** Database type: pg_catalog.bool */
  showIdmLines: ColumnType<boolean | null, never, never>

  /** Database type: pg_catalog.int4 */
  lineGapSize: ColumnType<number | null, never, never>

  /** Database type: pg_catalog.text */
  embeddedUuid: ColumnType<string | null, never, never>

  /** Database type: pg_catalog.timestamptz */
  archivedAt: ColumnType<Date | null, never, never>

  /** Database type: pg_catalog.text */
  wivMeasuringPointId: ColumnType<string | null, never, never>

  /** Database type: pg_catalog.timestamptz */
  wivBegin: ColumnType<Date | null, never, never>

  /** Database type: pg_catalog.timestamptz */
  wivEnd: ColumnType<Date | null, never, never>

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date | null, never, never>

  /** Database type: pg_catalog.uuid */
  createdBy: ColumnType<UserId | null, never, never>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date | null, never, never>

  /** Database type: pg_catalog.uuid */
  updatedBy: ColumnType<UserId | null, never, never>

  /** Database type: pg_catalog.timestamptz */
  deletedAt: ColumnType<Date | null, never, never>

  /** Database type: pg_catalog.uuid */
  deletedBy: ColumnType<UserId | null, never, never>

  /** Database type: pg_catalog.int4 */
  projectId: ColumnType<ProjectId, never, never>

  /** Database type: pg_catalog.text */
  projectName: ColumnType<string, never, never>

  /** Database type: pg_catalog.int4 */
  organizationId: ColumnType<OrganizationId, never, never>

  /** Database type: pg_catalog.text */
  organizationName: ColumnType<string, never, never>

  /** Database type: pg_catalog.jsonb */
  loggerHistory: ColumnType<unknown, never, never>

  /** Database type: pg_catalog.jsonb */
  cableHistory: ColumnType<unknown, never, never>

  /** Database type: pg_catalog.jsonb */
  mergeCableHistory: ColumnType<unknown, never, never>

  /** Database type: pg_catalog.jsonb */
  cableMetadata: ColumnType<unknown, never, never>

  /** Database type: pg_catalog.bool */
  showForecast: ColumnType<boolean, never, never>

  /** Database type: pg_catalog.text */
  forecastModelType: ColumnType<ForecastModelTypeId | null, never, never>

  /** Database type: pg_catalog.text */
  pageType: ColumnType<SitePageTypeId, never, never>

  /** Database type: pg_catalog.text */
  layout: ColumnType<SiteLayoutId, never, never>

  /**
   * Database type: pg_catalog.bool
   * Reverses the assumed order of thermal sensors that have no depth on the borehole dashboard (default false: channel 1 is the shallowest). Set only by global editors through PUT /site/:id. Superseded per sensor by a stored sensor_display_.depth_m (#4656).
   */
  flipThermalOrder: ColumnType<boolean, never, never>

  /** Database type: pg_catalog.numeric */
  radarReferenceHeight: ColumnType<number | null, never, never>
}

export type SiteWithHistoryView = Selectable<SiteWithHistoryViewTable>

export const siteWithHistoryView = z.object({
  id: legacySiteId,
  siteName: z.string(),
  visible: z.boolean().nullable().optional(),
  status: siteStatusType.nullable().optional(),
  sortOrder: z.number().nullable().optional(),
  includesArchivedData: z.boolean().nullable().optional(),
  internalNotes: z.string().nullable().optional(),
  customerNotes: z.string().nullable().optional(),
  lengthUnits: lengthUnitType,
  tempUnits: tempUnitType,
  geoLat: z.number().nullable().optional(),
  geoLon: z.number().nullable().optional(),
  geoElev: z.number().nullable().optional(),
  plotSnow: z.boolean().nullable().optional(),
  sonicHeight: z.number().nullable().optional(),
  labelSettingShowCableSerial: z.boolean().nullable().optional(),
  labelSettingSortBy: labelSortType.nullable().optional(),
  readingsVersion: z.number().nullable().optional(),
  useRecordDate: z.boolean().nullable().optional(),
  showSdmLines: z.boolean().nullable().optional(),
  showIdmLines: z.boolean().nullable().optional(),
  lineGapSize: z.number().nullable().optional(),
  embeddedUuid: z.string().nullable().optional(),
  archivedAt: z.coerce.date().nullable().optional(),
  wivMeasuringPointId: z.string().nullable().optional(),
  wivBegin: z.coerce.date().nullable().optional(),
  wivEnd: z.coerce.date().nullable().optional(),
  createdAt: z.coerce.date().nullable().optional(),
  createdBy: userId.nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
  updatedBy: userId.nullable().optional(),
  deletedAt: z.coerce.date().nullable().optional(),
  deletedBy: userId.nullable().optional(),
  projectId: projectId,
  projectName: z.string(),
  organizationId: organizationId,
  organizationName: z.string(),
  loggerHistory: z.unknown(),
  cableHistory: z.unknown(),
  mergeCableHistory: z.unknown(),
  cableMetadata: z.unknown(),
  showForecast: z.boolean(),
  forecastModelType: forecastModelTypeId.nullable().optional(),
  pageType: sitePageTypeId,
  layout: siteLayoutId,
  flipThermalOrder: z.boolean(),
  radarReferenceHeight: z.number().nullable().optional(),
}) as unknown as z.Schema<SiteWithHistoryView>
